import { useMemo } from 'react';
import {
  Search, Play, Clock, Loader2, ChevronDown, Plus, X, Check,
  Braces, Sigma, Hash, Tag,
} from 'lucide-react';
import { highlightLpql } from '@/lib/lpql-highlight';
import { useAutoTypewriter } from './useAutoTypewriter';
import { useStepSequencer, type SequencerStep } from './useStepSequencer';
import {
  DEMO_QUERY_1, DEMO_QUERY_2, DEMO_QUERY_2_APPEND,
  DEMO_LOGS, DEMO_FIELDS, TIMECHART_DATA, TIMECHART_TABLE,
  type DemoField,
} from './demo-data';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/*  Animation Steps                                                    */
/* ------------------------------------------------------------------ */

const SEARCH_STEPS: SequencerStep[] = [
  { id: 'typing-1', duration: 3000 },
  { id: 'running-1', duration: 1200 },
  { id: 'results-1', duration: 3500 },
  { id: 'typing-2', duration: 3200 },
  { id: 'time-dropdown', duration: 1200 },
  { id: 'time-select', duration: 800 },
  { id: 'running-2', duration: 1200 },
  { id: 'results-2', duration: 2500 },
  { id: 'viz-click', duration: 600 },
  { id: 'viz-show', duration: 4500 },
];

const SEARCH_STEPS_FAST: SequencerStep[] = [
  { id: 'typing-1', duration: 2000 },
  { id: 'running-1', duration: 800 },
  { id: 'results-1', duration: 2500 },
  { id: 'typing-2', duration: 2200 },
  { id: 'time-dropdown', duration: 800 },
  { id: 'time-select', duration: 500 },
  { id: 'running-2', duration: 800 },
  { id: 'results-2', duration: 1800 },
  { id: 'viz-click', duration: 400 },
  { id: 'viz-show', duration: 3000 },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const CATEGORY_ICON: Record<string, { icon: typeof Braces; color: string }> = {
  core: { icon: Braces, color: 'text-blue-500' },
  parsed: { icon: Hash, color: 'text-cyan-500' },
  eval: { icon: Sigma, color: 'text-amber-500' },
  attribute: { icon: Tag, color: 'text-violet-500' },
};

function levelClasses(level: string) {
  switch (level) {
    case 'error': return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'warn': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'info': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
}

/* ------------------------------------------------------------------ */
/*  Time Range Dropdown                                                */
/* ------------------------------------------------------------------ */

const TIME_OPTIONS = ['Last 15 minutes', 'Last 1 hour', 'Last 4 hours', 'Last 24 hours', 'Last 7 days', 'Last 30 days'];

function TimeRangeDropdown({ open, selectedIdx }: { open: boolean; selectedIdx: number }) {
  if (!open) return null;
  return (
    <div className="absolute right-0 top-10 z-50 w-36 sm:w-44 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl py-1 animate-fade-in">
      {TIME_OPTIONS.map((opt, i) => (
        <div key={opt} className={cn(
          'flex items-center justify-between px-3 py-1.5 text-xs transition-colors',
          i === selectedIdx ? 'bg-primary-600/20 text-primary-600 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
        )}>
          <span>{opt}</span>
          {i === selectedIdx && <Check className="h-3 w-3 text-primary-500" />}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mock Timechart SVG                                                 */
/* ------------------------------------------------------------------ */

function MockTimechart({ visible }: { visible: boolean }) {
  const W = 700, H = 200, padL = 40, padR = 10, padT = 10, padB = 30;
  const chartW = W - padL - padR, chartH = H - padT - padB;
  const maxVal = Math.max(...TIMECHART_DATA.map((d) => d.error + d.warn));
  const barW = chartW / TIMECHART_DATA.length;

  return (
    <div className={cn('transition-all duration-700', visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2')}>
      <div className="flex items-center gap-4 px-4 py-2">
        <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-red-500/70" /><span className="text-[10px] text-gray-500 dark:text-gray-400">error</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-amber-500/70" /><span className="text-[10px] text-gray-500 dark:text-gray-400">warn</span></div>
      </div>
      <div className="px-4 pb-3">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
          {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
            const y = padT + chartH * (1 - pct);
            return (<g key={pct}><line x1={padL} y1={y} x2={W - padR} y2={y} stroke="currentColor" strokeWidth={1} className="text-gray-200 dark:text-gray-800" /><text x={padL - 5} y={y + 3} textAnchor="end" className="fill-gray-400 dark:fill-gray-500" fontSize={9}>{Math.round(maxVal * pct)}</text></g>);
          })}
          {TIMECHART_DATA.map((d, i) => {
            const x = padL + i * barW;
            const errorH = (d.error / maxVal) * chartH, warnH = (d.warn / maxVal) * chartH;
            return (<g key={i}><rect x={x + 1} y={padT + chartH - errorH - warnH} width={barW - 2} height={errorH} rx={1} fill="rgba(239, 68, 68, 0.7)" /><rect x={x + 1} y={padT + chartH - warnH} width={barW - 2} height={warnH} rx={1} fill="rgba(245, 158, 11, 0.7)" /></g>);
          })}
          {TIMECHART_DATA.filter((_, i) => i % 4 === 0).map((d) => (
            <text key={d.hour} x={padL + d.hour * barW + barW / 2} y={H - 5} textAnchor="middle" className="fill-gray-400 dark:fill-gray-500" fontSize={9}>{`${String(d.hour).padStart(2, '0')}:00`}</text>
          ))}
        </svg>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mock Timechart Table                                               */
/* ------------------------------------------------------------------ */

function MockTimechartTable({ visible }: { visible: boolean }) {
  return (
    <div className={cn('transition-all duration-500 overflow-x-auto', visible ? 'opacity-100' : 'opacity-0')}>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            <th className="text-left px-4 py-2 w-40">_time</th>
            <th className="text-right px-4 py-2">error</th>
            <th className="text-right px-4 py-2">warn</th>
          </tr>
        </thead>
        <tbody>
          {TIMECHART_TABLE.map((row, i) => (
            <tr key={i} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30">
              <td className="px-4 py-1.5 font-mono text-gray-500 dark:text-gray-400">{row.time}</td>
              <td className="px-4 py-1.5 text-right font-mono text-red-500 dark:text-red-400">{row.error}</td>
              <td className="px-4 py-1.5 text-right font-mono text-amber-500 dark:text-amber-400">{row.warn}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mock Field Sidebar                                                 */
/* ------------------------------------------------------------------ */

function MockFieldSidebar({ fields, visible }: { fields: DemoField[]; visible: boolean }) {
  return (
    <div className={cn('w-48 shrink-0 border-r border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-700', visible ? 'opacity-100' : 'opacity-0')}>
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2 text-xs text-gray-400"><Search className="h-3 w-3" /><span className="text-gray-400 dark:text-gray-500">Filter fields...</span></div>
      </div>
      <div className="p-2 space-y-0.5">
        {fields.map((field) => {
          const cat = CATEGORY_ICON[field.category] || CATEGORY_ICON.core;
          const Icon = cat.icon;
          return (
            <div key={field.name}>
              <div className="flex items-center gap-1.5 px-1 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800/40">
                <Icon className={cn('h-3 w-3 shrink-0', cat.color)} />
                <span className="text-xs font-mono text-gray-800 dark:text-gray-200 truncate">{field.name}</span>
                <span className="ml-auto text-[9px] text-gray-400 dark:text-gray-500">{field.values.length}</span>
              </div>
              <div className="ml-[22px] space-y-0">
                {field.values.map((v) => (
                  <div key={v.name} className="flex items-center gap-1.5 px-1.5 py-0.5">
                    <span className="text-[11px] font-mono text-gray-700 dark:text-gray-300 truncate flex-1">{v.name}</span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">{v.pct}%</span>
                    <div className="w-12 h-[3px] rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                      <div className="h-full rounded-full bg-blue-400/70 dark:bg-blue-500/60 transition-all duration-1000" style={{ width: visible ? `${v.pct}%` : '0%' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main SearchDemo Component                                          */
/* ------------------------------------------------------------------ */

interface SearchDemoProps {
  enabled?: boolean;
  compact?: boolean;
  fast?: boolean;
}

export function SearchDemo({ enabled = true, compact = false, fast = false }: SearchDemoProps) {
  const { currentStepId, stepIndex } = useStepSequencer({
    steps: fast ? SEARCH_STEPS_FAST : SEARCH_STEPS,
    enabled,
    loop: true,
  });

  const step = currentStepId;
  const isTyping1 = step === 'typing-1';
  const isRunning1 = step === 'running-1';
  const showLogs = step === 'results-1' || step === 'typing-2';
  const isTyping2 = step === 'typing-2';
  const timeDropdownOpen = step === 'time-dropdown';
  const timeSelected = step === 'time-select';
  const isRunning2 = step === 'running-2';
  const showTable = step === 'results-2';
  const vizClicking = step === 'viz-click';
  const vizShowing = step === 'viz-show';
  const isRunning = isRunning1 || isRunning2;
  const isTyping = isTyping1 || isTyping2;
  const is24h = timeSelected || isRunning2 || showTable || vizClicking || vizShowing;
  const timeRange = is24h ? 'Last 24 hours' : 'Last 1 hour';
  const showResultsArea = showLogs || showTable || vizClicking || vizShowing;
  const activeResultsTab = vizClicking || vizShowing ? 'viz' : 'results';
  const sidebarVisible = showLogs;

  const { displayedText: typed1 } = useAutoTypewriter({ text: DEMO_QUERY_1, speed: 45, startDelay: 400, enabled: enabled && stepIndex === 0 });
  const { displayedText: typed2 } = useAutoTypewriter({ text: DEMO_QUERY_2_APPEND, speed: 50, startDelay: 200, enabled: enabled && stepIndex === 3 });

  const queryText = useMemo(() => {
    if (isTyping1) return typed1;
    if (isRunning1 || step === 'results-1') return DEMO_QUERY_1;
    if (isTyping2) return DEMO_QUERY_1 + typed2;
    return DEMO_QUERY_2;
  }, [step, typed1, typed2, isTyping1, isRunning1, isTyping2]);

  const highlighted = useMemo(() => highlightLpql(queryText), [queryText]);
  const visibleLogs = compact ? DEMO_LOGS.slice(0, 4) : DEMO_LOGS.slice(0, 6);
  const visibleFields = compact ? DEMO_FIELDS.slice(0, 3) : DEMO_FIELDS;

  return (
    <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-950 shadow-2xl overflow-hidden max-w-full min-w-0">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200/60 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400/60" />
          <div className="w-3 h-3 rounded-full bg-amber-400/60" />
          <div className="w-3 h-3 rounded-full bg-emerald-400/60" />
        </div>
        <div className="flex-1 mx-6">
          <div className="h-5 rounded-md bg-gray-200/60 dark:bg-gray-800 max-w-xs mx-auto flex items-center justify-center">
            <span className="text-[10px] text-gray-500">app.logpulse.io/search</span>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-end h-8 sm:h-9 bg-gray-100 dark:bg-gray-900">
        <div className="flex items-end overflow-hidden">
          <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-7 sm:h-8 bg-white dark:bg-gray-950 border-t border-x border-gray-200 dark:border-gray-800 rounded-t-lg text-[10px] sm:text-xs font-medium text-gray-900 dark:text-white" style={{ marginBottom: '-1px' }}>
            <span className="truncate max-w-[80px] sm:max-w-[120px]">Production Errors</span>
            <span className="text-[9px] sm:text-[10px] rounded bg-gray-200 dark:bg-gray-700 px-1 sm:px-1.5 py-0.5">2</span>
            <X className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-gray-400 dark:text-gray-500" />
          </div>
          <div className="hidden sm:flex items-center gap-1.5 px-3 h-8 text-xs font-medium text-gray-400 dark:text-gray-500">
            <span className="truncate max-w-[100px]">API Latency</span>
            <span className="text-[10px] rounded bg-gray-200/80 dark:bg-gray-800/80 px-1.5 py-0.5">1</span>
          </div>
        </div>
        <div className="flex items-center justify-center h-full w-9 text-gray-400 dark:text-gray-500 ml-auto">
          <Plus className="h-3.5 w-3.5" />
        </div>
      </div>

      {/* Query editor */}
      <div className="border-b border-gray-200 dark:border-gray-800 relative z-10">
        <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 pt-2 sm:pt-3 pb-2 relative">
          <div className="flex items-center gap-1 mr-auto">
            <button className="hidden sm:flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
              <Braces className="h-3.5 w-3.5" /><span>Syntax</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div className={cn(
              'flex items-center gap-1 sm:gap-1.5 h-7 px-1.5 sm:px-2.5 rounded-md border text-[10px] sm:text-xs transition-all duration-300',
              (timeDropdownOpen || is24h) ? 'border-primary-500/50 bg-primary-50 dark:bg-gray-800 text-primary-600 dark:text-primary-300' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            )}>
              <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 dark:text-gray-500" />
              <span className="whitespace-nowrap">{timeRange}</span>
              <ChevronDown className={cn('h-3 w-3 text-gray-400 dark:text-gray-500 transition-transform duration-200', timeDropdownOpen && 'rotate-180')} />
            </div>
            <button className="flex items-center gap-1 sm:gap-1.5 h-7 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs font-medium bg-primary-600 text-white hover:bg-primary-500">
              {isRunning ? <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 animate-spin" /> : <Play className="h-3 w-3 sm:h-3.5 sm:w-3.5" />}
              <span>{isRunning ? 'Searching...' : 'Search'}</span>
            </button>
          </div>
          <TimeRangeDropdown open={timeDropdownOpen} selectedIdx={timeDropdownOpen ? 1 : 3} />
          {timeSelected && <TimeRangeDropdown open={true} selectedIdx={3} />}
        </div>

        {/* Query input */}
        <div className="px-3 sm:px-4 pb-3">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 sm:p-3 font-mono text-xs sm:text-sm h-[40px] sm:h-[44px] overflow-hidden" style={{ contain: 'inline-size' }}>
            <pre className="whitespace-nowrap leading-relaxed">
              {highlighted}
              {isTyping && <span className="inline-block w-[2px] h-4 bg-gray-800 dark:bg-gray-300 align-text-bottom animate-pulse ml-[1px]" />}
            </pre>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-gray-100 dark:bg-gray-800 overflow-hidden">
          {isRunning && <div className="h-full bg-primary-500 animate-progress-bar" />}
        </div>
      </div>

      {/* Results area */}
      <div className="flex h-[250px] sm:h-[310px] overflow-hidden">
        {!compact && sidebarVisible && (
          <div className="hidden lg:block"><MockFieldSidebar fields={visibleFields} visible={sidebarVisible} /></div>
        )}
        <div className="flex-1 min-w-0">
          {showResultsArea && (
            <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-200 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-800 dark:text-gray-200">{showLogs ? `${DEMO_LOGS.length} events` : '24 buckets'}</span>
              <span>in {showLogs ? '0.023s' : '0.118s'}</span>
              <div className="ml-auto flex items-center gap-2">
                <span className={cn('px-2 py-0.5 rounded transition-colors', activeResultsTab === 'results' ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-400 dark:text-gray-500')}>Results</span>
                <span className={cn('px-2 py-0.5 rounded transition-colors', activeResultsTab === 'viz' ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-400 dark:text-gray-500')}>Visualization</span>
              </div>
            </div>
          )}

          {showLogs && (
            <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
              {visibleLogs.map((log, i) => (
                <div key={i} className="flex items-start gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all duration-300">
                  <span className="hidden sm:inline font-mono text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap pt-0.5 shrink-0">{log.timestamp}</span>
                  <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 mt-0.5', levelClasses(log.level))}>{log.level.toUpperCase()}</span>
                  <span className="font-mono text-xs sm:text-sm text-gray-800 dark:text-gray-100 leading-relaxed break-words min-w-0">{log.event}</span>
                </div>
              ))}
            </div>
          )}

          {showTable && <MockTimechartTable visible={true} />}
          {(vizClicking || vizShowing) && <MockTimechart visible={vizShowing} />}

          {!showResultsArea && (
            <div className="flex items-center justify-center h-[200px] text-gray-400 dark:text-gray-600 text-sm">
              {isRunning ? (
                <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-primary-500" /><span>Searching across 2.4B events...</span></div>
              ) : <span>Type a query and press Search</span>}
            </div>
          )}

          {showLogs && !compact && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">FIELDS:</span>
                {['source', 'level', 'host', 'status_code'].map((f) => (
                  <span key={f} className="inline-flex items-center gap-1 rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-mono">
                    <span className="text-gray-500">{f}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
